import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PRESS_RELEASE_LETTERHEAD, PRESS_RELEASE_PDF } from '../links'

const PressRelease = () => {
  useEffect(() => {
    document.title = 'Press Release - Haight & Ashbury'
  }, [])

  const pdfHref = encodeURI(PRESS_RELEASE_PDF)

  return (
    <div className="bg-neutral-100">
      <section className="px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <article className="press-doc max-w-[45rem] mx-auto bg-white shadow-sm border border-neutral-200 px-5 py-8 sm:px-10 sm:py-12">
          <p className="mb-6">
            <a
              href={pdfHref}
              download="Doolan_Larson_Sep_2026_Press_Release.pdf"
              className="press-download inline-flex items-center justify-center min-h-11 font-bold"
            >
              Download press release (PDF)
            </a>
          </p>

          <img
            src={PRESS_RELEASE_LETTERHEAD}
            alt="San Francisco Heritage letterhead"
            className="block w-full h-auto mb-8"
          />

          <p className="font-bold mb-6">FOR IMMEDIATE RELEASE</p>

          <p className="mb-8">
            <strong>SF Heritage Media Contact:</strong>
            <br />
            Woody LaBounty
            <br />
            President &amp; CEO
            <br />
            <a href="tel:+14154413000">415-441-3000</a> x 104
            <br />
            <a href="mailto:wlabounty@sfheritage.org">wlabounty@sfheritage.org</a>
          </p>

          <h1 className="mb-6">
            San Francisco Heritage Announces Plans for Music and Cultural Center
            with KEXP at Haight and Ashbury
          </h1>

          <p className="italic font-bold mb-8">
            The citywide advocacy group is exploring a new partnership with nonprofit
            radio station KEXP and Blue Bear School of Music to reuse the historic
            property at the corner of Haight and Ashbury.
          </p>

          <div className="space-y-6">
            <p>
              <strong>September 17, 2026 [SAN FRANCISCO]</strong>
              &ndash;Today, SF Heritage announces exploring a partnership with public
              broadcaster KEXP and their project partner Blue Bear School of Music to
              create a new cultural center at the historic Doolan-Larson House and
              Storefronts on the corner of Haight and Ashbury streets.
            </p>

            <p>
              &ldquo;Art and music have been part of what makes San Francisco special
              for generations. Today, we celebrate not only that legacy, but also the
              creativity that is still driving our economic recovery,&rdquo; said{' '}
              <strong>Mayor Daniel Lurie</strong>. &ldquo;Innovative partnerships like
              this one help us reimagine what&apos;s possible and find new ways to
              activate our neighborhoods. Our artists and cultural spaces make our
              city stronger, and this new home for music and culture at Haight and
              Ashbury will inspire generations to come.&rdquo;
            </p>

            <p>
              The plans for the partnership between SF Heritage, KEXP, and Blue Bear
              School of Music call for creating a welcoming third space for year-round
              daily activation, a rooftop venue for live music and community
              celebrations, radio broadcast facilities, and a full floor dedicated to
              individual and group music lessons. SF Heritage will finalize the
              adaptive reuse plan with KEXP and Blue Bear over the coming months,
              soliciting community input and determining feasibility for construction
              and project financing.
            </p>

            <p>
              &ldquo;San Francisco&apos;s music and arts community has always been one
              of our greatest strengths, and Haight-Ashbury is at the heart of that
              legacy,&rdquo; said <strong>District 5 Supervisor Bilal Mahmood</strong>.
              &ldquo;Bringing KEXP and Blue Bear together at the Doolan-Larson House
              creates an opportunity to preserve an iconic piece of our history while
              investing in the artists, musicians, students, and communities who will
              shape our cultural future. We also plan to work with local merchants to
              make sure this investment brings more foot traffic, supports small
              businesses, and strengthens the surrounding neighborhood.&rdquo;
            </p>

            <p>
              The historic{' '}
              <Link to="/">Doolan-Larson House</Link> is a landmark of the
              counterculture and a symbol of San Francisco&apos;s ongoing role as a
              nexus of cultural and political change. San Francisco Heritage has
              stewarded the property since 2017 after it was donated by longtime
              resident and owner Norm Larson. This gift marked the beginning of a
              years-long, inclusive public process to identify partners for the
              adaptive reuse and activation of the property. Three teams submitted
              their full proposals in June, with a final selection made by the SF
              Heritage board in August.
            </p>

            <p>
              &ldquo;SF Heritage is the steward of a historic property at the most
              famous intersection in the world,&rdquo; said{' '}
              <strong>Woody LaBounty, San Francisco Heritage President and CEO</strong>.
              &ldquo;With KEXP and Blue Bear School of Music, we see an exciting
              future of preserving the integrity of the Doolan-Larson building,
              honoring the spirit of the counterculture, and ensuring the Haight
              Ashbury community thrives both economically and culturally.&rdquo;
            </p>

            <p>
              As part of planning, the KEXP team has drawn on the specialized
              knowledge of Page &amp; Turnbull, a San Francisco-based architecture firm
              dedicated to historic preservation and adaptive reuse, and Gensler, a
              global architecture and design firm founded in San Francisco.
            </p>

            <p>
              &ldquo;KEXP is honored to move forward with the next steps to realize
              our proposal for the adaptive reuse of the Doolan-Larson House and
              Storefronts,&rdquo; said <strong>Ethan Raup, KEXP CEO</strong>. &ldquo;We
              are deeply inspired by the prospect of establishing a lasting partnership
              with SF Heritage and Blue Bear School of Music, built on our shared
              commitment to culture, legacy, and the unique identity of this iconic
              intersection. We approach this opportunity with profound respect for the
              stewardship required to preserve and uphold both the tangible structure
              of the Doolan-Larson buildings, and the living counterculture of
              Haight-Ashbury.&rdquo;
            </p>

            <p>
              &ldquo;Working together, KEXP and Blue Bear will build one of the
              country&apos;s most unique, innovative, and highly integrated civic music
              hubs,&rdquo; said{' '}
              <strong>Steve Savage, Blue Bear School of Music Executive Director</strong>.
              &ldquo;By selecting this project, SF Heritage is opening a space for us
              to create a complete music ecosystem: one that combines broadcasting,
              performance, discovery, education, and community under one roof.&rdquo;
            </p>

            <p>
              &ldquo;This new art and cultural space presents an opportunity to
              preserve the legacy of the Haight-Ashbury neighborhood, while providing
              artists, musicians, students, and the public a new artistic hub to
              gather, create, connect and experience music,&rdquo; said{' '}
              <strong>Matthew Goudeau, Executive Director of Arts and Culture</strong>.
              &ldquo;This partnership is a powerful example of how we can all work
              together to preserve the city&apos;s iconic cultural heritage and help
              build San Francisco&apos;s creative future.&rdquo;
            </p>

            <p>
              &ldquo;This is exactly the kind of dynamic, forward-thinking partnership
              we love to see. Through the inventive adaptive reuse of a historic
              building, this effort will strengthen the legacy of live music in the
              Haight and help fuel our creative economy,&rdquo; said{' '}
              <strong>
                Anne Taupier, Office of Economic and Workforce Development Executive
                Director
              </strong>
              .{' '}
              &ldquo;Over the past year, we&apos;ve seen a real resurgence of
              nightlife and culture in the neighborhood, from the recent Train concert
              to the return of The Deluxe, and now live music at The Mellow. This new
              arts center will help anchor that momentum and keep it growing.&rdquo;
            </p>

            <p>
              &ldquo;The Doolan-Larson building represents a major chapter in San
              Francisco&apos;s and the nation&apos;s cultural history,&rdquo; said{' '}
              <strong>
                Brent Glass, former Director of the Smithsonian National Museum of
                American History
              </strong>{' '}
              who helped advise SF Heritage during the selection process. &ldquo;KEXP
              is the perfect partner to secure its future, the legacy of Norm Larson,
              and the mission of San Francisco Heritage. I can&apos;t wait to see their
              plan unfold in the next few years.&rdquo;
            </p>

            <p>
              KEXP and Blue Bear School of Music will be at the Haight Ashbury Street
              Fair on Sunday, September 20, to share more about the project and support
              participating artists, local merchants, and organizations.
            </p>

            <p className="text-center tracking-[0.2em] py-2">###</p>

            <h2>About SF Heritage</h2>
            <p>
              Founded in 1971, San Francisco Heritage is a 501(c)(3) membership
              organization that preserves and enhances San Francisco&apos;s unique
              architectural and cultural identity. Their programs and partnerships
              interpret, share, and celebrate the shared histories, landscapes, and
              communities of the city to instill optimism, belonging, and purpose.
            </p>

            <h2>About KEXP</h2>
            <p>
              KEXP is an international community of music lovers and music makers, and
              a nonprofit organization fostering relationship and community building
              through broadcast, online, and in-person music programming. KEXP operates
              one of the most influential music radio stations in the world,
              championing music spanning eras, styles, and traditions. KEXP Radio
              broadcasts at 92.7 FM in the Bay Area and 90.3 FM in Seattle, with live
              and on-demand listening on their website and mobile apps. On YouTube,{' '}
              <em>Live on KEXP</em> sessions featuring exclusive in-studio
              performances and interviews from emerging and established artists are
              recognized globally, reaching 7 million viewers each month. From a public
              facility at Seattle Center, KEXP produces hundreds of live performances
              and music events each year, many of which are open to public audiences at
              no charge. KEXP&apos;s mission is to enrich your life by championing
              music and discovery.
            </p>

            <h2>About Blue Bear School of Music</h2>
            <p>
              Blue Bear School of Music is San Francisco&apos;s preeminent school for
              rock, blues, folk, pop, songwriting, DJ, and electronic music. For 55+
              years, Blue Bear has helped over 50,000 aspiring musicians of all ages
              and skill levels discover the joy of making music. At the heart of Blue
              Bear&apos;s nonprofit mission is a simple philosophy:{' '}
              <em>play the music you love.</em> In this vibrant and welcoming
              community, each student&apos;s individual talents and goals are
              recognized and supported. Beyond its classrooms, Blue Bear partners with
              leading San Francisco social service organizations to bring the
              transformational power of music to underserved communities throughout the
              Bay Area. Last year, Blue Bear provided more than 1,000 free music
              programs for children, delivered 215 free programs in San Francisco
              schools, and employed 69 professional teaching artists.
            </p>
          </div>
        </article>
      </section>
    </div>
  )
}

export default PressRelease
